import { createState } from './state.js';
import { qs, qsa, clear, setText } from '../utils/dom.js';
import { parseGPX } from '../gpx/parse.js';
import { parseFIT } from '../fit/parse.js';
import {
  analyzeSegments,
  createGradientSummary,
  groupSegmentsIntoSections,
  calculateSectionStatistics
} from '../gpx/segments.js';
import {
  calculateUltraPacing,
  costOfRunning,
  exportUltraPacingCsv
} from '../ultra/ultraPacing.js';
import { extractActualPerformance, hasTimestamps } from '../analysis/actualPerformance.js';
import { computeComparison } from '../analysis/comparison.js';
import { drawGradientChart } from '../render/gradientChart.js';
import { drawProfileChart, setupProfileChartInteractions } from '../render/profileChart.js';
import { renderProfileDetails } from '../render/profileDetails.js';
import {
  renderGradientSummaryTable,
  renderSectionsTable,
  renderSegmentDetailsTable,
  renderSectionStats,
  renderSummaryStats,
  renderUltraSummary
} from '../render/tables.js';
import { renderComparisonSummary, renderComparisonTable, drawFadeChart } from '../render/comparison.js';
import { analyzeRouteDeviation } from '../analysis/routeDeviation.js';
import { renderRouteDeviation } from '../render/routeDeviation.js';
import { VERSION } from '../version.js';

function switchTab(rootEl, tabName) {
  qsa(rootEl, '.tab-content').forEach(tab => tab.classList.remove('active'));
  qsa(rootEl, '.tab-header').forEach(header => header.classList.remove('active'));

  const targetTab = qs(rootEl, `#${tabName}-tab`);
  if (targetTab) targetTab.classList.add('active');

  const clickedHeader = qs(rootEl, `.tab-header[data-tab="${tabName}"]`);
  if (clickedHeader) clickedHeader.classList.add('active');
}

function downloadTextFile(filename, content, mime = 'text/plain') {
  const blob = new Blob([content], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function ensureMapInitialized({ state, mapEl }) {
  if (state.map || !globalThis.L) return;
  state.map = globalThis.L.map(mapEl);
  globalThis.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  }).addTo(state.map);
}

function setLoading(rootEl, isLoading) {
  const loadingEl = qs(rootEl, '#loading');
  if (loadingEl) loadingEl.style.display = isLoading ? 'block' : 'none';
}

function showError(rootEl, message) {
  const errorDiv = qs(rootEl, '#error');
  const resultsDiv = qs(rootEl, '#results');
  if (errorDiv) {
    errorDiv.style.display = 'block';
    setText(errorDiv, message);
  }
  if (resultsDiv) resultsDiv.style.display = 'none';
}

function hideError(rootEl) {
  const errorDiv = qs(rootEl, '#error');
  if (errorDiv) {
    errorDiv.style.display = 'none';
    setText(errorDiv, '');
  }
}

function computeUltraPacing({ sections, segments, totalDistanceMeters, atDistanceKm }) {
  if (!(atDistanceKm > 0)) return null;
  if (totalDistanceMeters < 25000) return null;
  const totalElevGain = segments.reduce((sum, seg) => sum + seg.elevationGain, 0);
  return calculateUltraPacing(atDistanceKm, totalDistanceMeters / 1000, totalElevGain, sections);
}

function computeGapDistances(sections) {
  const costFlat = costOfRunning(0);
  let totalGAPDistance = 0;
  sections.forEach(section => {
    const gapFactor = costOfRunning(section.averageGradient / 100) / costFlat;
    section.gapDistance = section.totalDistance * gapFactor;
    totalGAPDistance += section.gapDistance;
  });
  return totalGAPDistance;
}

function getFileType(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.fit')) return 'fit';
  return 'gpx';
}

async function readFileAsTrackpoints(file) {
  const type = getFileType(file);
  if (type === 'fit') {
    const buffer = await file.arrayBuffer();
    return { trackpoints: await parseFIT(buffer), fileType: 'fit' };
  }
  const text = await file.text();
  return { trackpoints: parseGPX(text), fileType: 'gpx', gpxText: text };
}

function displayResults({ rootEl, state, elements }, { trackpoints, segments, totalDistance, gradientSummary, sections }) {
  const minRunnable = parseFloat(elements.minRunnableInput.value);
  const maxRunnable = parseFloat(elements.maxRunnableInput.value);

  const atDistance = parseFloat(elements.atDistanceInput.value);
  const ultraPacing = computeUltraPacing({
    sections,
    segments,
    totalDistanceMeters: totalDistance,
    atDistanceKm: atDistance
  });

  const totalGAPDistance = computeGapDistances(sections);

  state.fullAnalysisData = {
    trackpoints,
    segments,
    totalDistance,
    gradientSummary,
    sections,
    ultraPacing,
    totalGAPDistance
  };
  state.hoveredSection = null;
  state.selectedSection = null;

  // Summary stats
  const runnableSegments = segments.filter(seg => seg.averageGradient >= minRunnable && seg.averageGradient <= maxRunnable);
  const runnableDistance = runnableSegments.reduce((sum, seg) => sum + seg.distance, 0);
  const runnablePercentage = totalDistance > 0 ? ((runnableDistance / totalDistance) * 100).toFixed(1) : '0';
  const totalElevationGain = segments.reduce((sum, seg) => sum + seg.elevationGain, 0);
  const totalElevationLoss = segments.reduce((sum, seg) => sum + seg.elevationLoss, 0);
  const avgGradient = segments.length > 0
    ? (segments.reduce((sum, seg) => sum + seg.averageGradient, 0) / segments.length).toFixed(1)
    : '0.0';

  renderSummaryStats(elements.summaryStats, {
    totalDistanceKm: totalDistance / 1000,
    totalGAPDistanceKm: totalGAPDistance / 1000,
    runnableDistanceKm: runnableDistance / 1000,
    runnablePercentage,
    totalSegments: segments.length,
    totalSections: sections.length,
    totalElevationGainM: totalElevationGain,
    totalElevationLossM: totalElevationLoss,
    avgGradientPercent: avgGradient
  });

  renderUltraSummary(elements.ultraSummary, ultraPacing, {
    onExport: () => {
      if (!ultraPacing) return;
      const csv = exportUltraPacingCsv(ultraPacing);
      downloadTextFile('ultra_pacing_strategy.csv', csv, 'text/csv');
    }
  });

  // Charts
  drawProfileChart({ canvas: elements.profileCanvas, container: elements.profileChartContainer, state });
  drawGradientChart(elements.gradientCanvas, gradientSummary, minRunnable, maxRunnable);

  // Tables
  renderGradientSummaryTable(elements.gradientSummaryTable, gradientSummary);
  renderSectionsTable(elements.sectionsTable, sections);
  renderSegmentDetailsTable(elements.segmentDetailsTable, segments);

  const sectionStats = calculateSectionStatistics(sections);
  renderSectionStats(elements.sectionStats, sectionStats);

  // Comparison: planned vs actual (only if timestamps exist and ultra pacing is computed)
  const hasTime = hasTimestamps(trackpoints);
  const comparisonTabHeader = elements.comparisonTabHeader;

  if (hasTime && ultraPacing) {
    const actualPerf = extractActualPerformance(sections, trackpoints);
    state.actualPerformance = actualPerf;

    const comparison = computeComparison(ultraPacing, actualPerf);
    state.comparison = comparison;

    if (comparison && comparisonTabHeader) {
      comparisonTabHeader.style.display = '';
      renderComparisonSummary(elements.comparisonSummary, comparison);
      renderComparisonTable(elements.comparisonTable, comparison);
      drawFadeChart(elements.fadeChart, comparison);
    }
  } else {
    state.actualPerformance = null;
    state.comparison = null;
    if (comparisonTabHeader) comparisonTabHeader.style.display = 'none';
    if (elements.comparisonSummary) clear(elements.comparisonSummary);
    if (elements.comparisonTable) clear(elements.comparisonTable);
  }

  // Route deviation: course GPX vs recorded route
  if (elements.routeDeviationSummary) {
    clear(elements.routeDeviationSummary);
  }
  if (state.courseData && hasTime && elements.routeDeviationSummary) {
    try {
      const courseTrackpoints = parseGPX(state.courseData);
      if (courseTrackpoints.length >= 2) {
        const segmentDistance = parseInt(elements.segmentDistanceInput.value, 10);
        const { segments: courseSegments } = analyzeSegments(courseTrackpoints, segmentDistance);
        const courseSections = groupSegmentsIntoSections(courseSegments);

        const deviation = analyzeRouteDeviation(trackpoints, courseTrackpoints, sections, courseSections);
        renderRouteDeviation(elements.routeDeviationSummary, deviation);

        // Ensure comparison tab is visible even if no ultra pacing
        if (comparisonTabHeader) comparisonTabHeader.style.display = '';
      }
    } catch (e) {
      // Silently skip route deviation on error
    }
  }

  // Show results + default tab
  elements.results.style.display = 'block';
  switchTab(rootEl, 'profile');

  renderProfileDetails(elements.profileDetails, state);
}

async function analyzeFile({ rootEl, state, elements }) {
  const file = elements.fileInput.files?.[0];
  if (!file) {
    showError(rootEl, 'Please select a GPX or FIT file');
    return;
  }

  try {
    setLoading(rootEl, true);
    elements.results.style.display = 'none';
    hideError(rootEl);

    state.selectedSection = null;
    state.hoveredSection = null;
    state.actualPerformance = null;
    state.comparison = null;
    renderProfileDetails(elements.profileDetails, state);

    const { trackpoints, fileType, gpxText } = await readFileAsTrackpoints(file);
    state.fileType = fileType;

    // Read course GPX if provided
    const courseFile = elements.courseGpxFile?.files?.[0];
    if (courseFile) {
      state.courseData = await courseFile.text();
    } else {
      state.courseData = null;
    }

    // For map display, we need GPX text (FIT files don't have native Leaflet-GPX support)
    if (gpxText) {
      state.gpxData = gpxText;
    } else {
      state.gpxData = null;
    }

    // Update map if GPX text is available
    if (state.map && globalThis.L?.GPX && state.gpxData) {
      if (state.gpxLayer) state.map.removeLayer(state.gpxLayer);
      if (state.highlightLayer) state.map.removeLayer(state.highlightLayer);
      if (state.selectionLayer) state.map.removeLayer(state.selectionLayer);
      state.highlightLayer = null;
      state.selectionLayer = null;

      state.gpxLayer = new globalThis.L.GPX(state.gpxData, {
        async: true,
        marker_options: {
          startIconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet-gpx/1.7.0/pin-icon-start.png',
          endIconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet-gpx/1.7.0/pin-icon-end.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet-gpx/1.7.0/pin-shadow.png'
        },
        polyline_options: {
          color: 'blue',
          opacity: 0.75,
          weight: 3,
          lineCap: 'round'
        }
      }).on('loaded', function (e) {
        state.map.fitBounds(e.target.getBounds());
      }).addTo(state.map);
    } else if (state.map && globalThis.L && fileType === 'fit') {
      // For FIT files, draw polyline from trackpoints directly
      if (state.gpxLayer) state.map.removeLayer(state.gpxLayer);
      if (state.highlightLayer) state.map.removeLayer(state.highlightLayer);
      if (state.selectionLayer) state.map.removeLayer(state.selectionLayer);
      state.highlightLayer = null;
      state.selectionLayer = null;

      const latlngs = trackpoints.map(tp => [tp.lat, tp.lon]);
      state.gpxLayer = globalThis.L.polyline(latlngs, {
        color: 'blue',
        opacity: 0.75,
        weight: 3,
        lineCap: 'round'
      }).addTo(state.map);
      state.map.fitBounds(state.gpxLayer.getBounds());
    }

    if (trackpoints.length < 2) throw new Error('File must contain at least 2 trackpoints');

    const segmentDistance = parseInt(elements.segmentDistanceInput.value, 10);
    const { segments, totalDistance } = analyzeSegments(trackpoints, segmentDistance);
    const gradientSummary = createGradientSummary(segments);
    const sections = groupSegmentsIntoSections(segments);

    displayResults({ rootEl, state, elements }, { trackpoints, segments, totalDistance, gradientSummary, sections });
  } catch (error) {
    showError(rootEl, `Error analyzing file: ${error.message}`);
  } finally {
    setLoading(rootEl, false);
  }
}

/**
 * Initialize one analyzer instance rooted at `rootEl`.
 * All DOM queries are scoped to this root.
 */
export function init(rootEl) {
  const state = createState();

  const elements = {
    fileInput: qs(rootEl, '#gpxFile'),
    analyzeBtn: qs(rootEl, '#analyzeBtn'),
    segmentDistanceInput: qs(rootEl, '#segmentDistance'),
    minRunnableInput: qs(rootEl, '#minRunnable'),
    maxRunnableInput: qs(rootEl, '#maxRunnable'),
    atDistanceInput: qs(rootEl, '#atDistance'),

    results: qs(rootEl, '#results'),
    summaryStats: qs(rootEl, '#summaryStats'),
    ultraSummary: qs(rootEl, '[data-ultra-summary]'),

    profileCanvas: qs(rootEl, '#profileChart'),
    profileChartContainer: qs(rootEl, '#profile-chart-container'),
    profileDetails: qs(rootEl, '#profile-details'),

    gradientCanvas: qs(rootEl, '#gradientChart'),
    gradientSummaryTable: qs(rootEl, '#gradientSummary'),
    sectionStats: qs(rootEl, '#sectionStats'),
    sectionsTable: qs(rootEl, '#sectionsTable'),
    segmentDetailsTable: qs(rootEl, '#segmentDetails'),

    showMapToggle: qs(rootEl, '#showMap'),
    mapContainer: qs(rootEl, '#map-container'),
    mapEl: qs(rootEl, '#map'),
    versionEl: qs(rootEl, '#gpx-analyzer-version'),

    comparisonTabHeader: qs(rootEl, '#comparison-tab-header'),
    comparisonSummary: qs(rootEl, '#comparisonSummary'),
    comparisonTable: qs(rootEl, '#comparisonTable'),
    fadeChart: qs(rootEl, '#fadeChart'),

    courseGpxFile: qs(rootEl, '#courseGpxFile'),
    routeDeviationSummary: qs(rootEl, '#routeDeviationSummary')
  };

  if (!elements.fileInput || !elements.analyzeBtn) return;

  // Enable Analyze when file selected
  elements.fileInput.addEventListener('change', () => {
    elements.analyzeBtn.disabled = !(elements.fileInput.files && elements.fileInput.files.length > 0);
  });

  // Analyze click
  elements.analyzeBtn.addEventListener('click', () => analyzeFile({ rootEl, state, elements }));

  // Tab clicks
  const tabHeaders = qs(rootEl, '.tab-headers');
  tabHeaders?.addEventListener('click', (e) => {
    const header = e.target?.closest?.('.tab-header');
    if (!header) return;
    const tab = header.dataset.tab;
    if (!tab) return;
    switchTab(rootEl, tab);
  });

  // Map toggle
  elements.showMapToggle?.addEventListener('change', () => {
    if (!elements.mapContainer || !elements.mapEl) return;
    if (elements.showMapToggle.checked) {
      elements.mapContainer.style.display = 'block';
      ensureMapInitialized({ state, mapEl: elements.mapEl });

      if (state.map && state.fullAnalysisData?.trackpoints && !state.gpxLayer) {
        if (state.gpxData && globalThis.L?.GPX) {
          state.gpxLayer = new globalThis.L.GPX(state.gpxData, { async: true }).on('loaded', function (e) {
            state.map.fitBounds(e.target.getBounds());
          }).addTo(state.map);
        } else if (globalThis.L) {
          const latlngs = state.fullAnalysisData.trackpoints.map(tp => [tp.lat, tp.lon]);
          state.gpxLayer = globalThis.L.polyline(latlngs, {
            color: 'blue', opacity: 0.75, weight: 3, lineCap: 'round'
          }).addTo(state.map);
          state.map.fitBounds(state.gpxLayer.getBounds());
        }
      }
    } else {
      elements.mapContainer.style.display = 'none';
    }
  });

  // Profile chart interactions (wired once; guarded internally)
  setupProfileChartInteractions({
    canvas: elements.profileCanvas,
    state,
    draw: () => drawProfileChart({ canvas: elements.profileCanvas, container: elements.profileChartContainer, state }),
    onSelectionChanged: () => {
      renderProfileDetails(elements.profileDetails, state);
      drawProfileChart({ canvas: elements.profileCanvas, container: elements.profileChartContainer, state });
    }
  });

  // Version display
  if (elements.versionEl) elements.versionEl.textContent = `v${VERSION}`;

  // Store state for debugging
  rootEl.__taktykGpxAnalyzerState = state;
}
